/**
 * Utility functions for handling calculated fields
 */

/**
 * Parse a date string or Date object and return a Date
 * @param {String|Date} dateValue - The date to parse
 * @returns {Date|null} - Parsed date or null if invalid
 */
function parseDate (dateValue) {
  if (!dateValue) return null
  if (dateValue instanceof Date) return dateValue

  // Only parse if it's a string (not a number)
  if (typeof dateValue !== 'string') return null

  // Check if it looks like a date string (contains dashes or slashes)
  // This prevents pure numbers from being parsed as dates
  if (!dateValue.includes('-') && !dateValue.includes('/')) return null

  // Try parsing ISO date format (YYYY-MM-DD) or other date formats
  const date = new Date(dateValue)
  if (!isNaN(date.getTime()) && date.getFullYear() > 1900 && date.getFullYear() < 2100) {
    return date
  }

  return null
}

/**
 * Calculate the number of days between two dates
 * @param {Date} date1 - First date
 * @param {Date} date2 - Second date
 * @returns {Number} - Number of days (can be negative)
 */
// Currently unused but kept for potential future use
// eslint-disable-next-line no-unused-vars
function daysBetween (date1, date2) {
  const msPerDay = 1000 * 60 * 60 * 24
  const utc1 = Date.UTC(date1.getFullYear(), date1.getMonth(), date1.getDate())
  const utc2 = Date.UTC(date2.getFullYear(), date2.getMonth(), date2.getDate())
  return Math.floor((utc2 - utc1) / msPerDay)
}

/**
 * Calculate a text template with string interpolation
 * @param {String} template - The template string wrapped in quotes with {field} references
 * @param {Object} dataModel - The data object containing values for referenced fields
 * @returns {String} - The interpolated text string
 */
function calculateTextTemplate (template, dataModel) {
  // Remove surrounding quotes
  let text = template.slice(1, -1)

  // Find all field references {field_name} or {field_name:format}
  const fieldMatches = text.match(/{([^}]+)}/g)
  if (!fieldMatches) {
    return text // Return as-is if no field references
  }

  const today = new Date()

  // Track if any referenced field is missing
  let hasMissingField = false

  // Replace each field reference with its formatted value
  fieldMatches.forEach(match => {
    if (hasMissingField) return // Skip processing if already found missing field

    const content = match.slice(1, -1) // Remove the curly braces

    // Check for inline format specifier: {field:format} or {field:.2f}
    // Also support inline calculations: {field + 5:.2f} or {field1 * field2:$}
    let expression = content
    let formatSpec = null

    if (content.includes(':')) {
      const parts = content.split(':')
      expression = parts[0].trim()
      formatSpec = parts[1].trim()
    }

    // Handle special date keywords (only if it's a simple field reference, not an expression)
    if (!expression.match(/[+\-*/()]/)) {
      if (expression.toLowerCase() === 'today' || expression.toLowerCase() === 'now') {
        const dateStr = today.toLocaleDateString()
        text = text.replace(match, dateStr)
        return
      }

      if (expression.toLowerCase() === 'today_date' || expression.toLowerCase() === 'current_date') {
        const dateStr = today.toLocaleDateString()
        text = text.replace(match, dateStr)
        return
      }

      // Check if it's a date literal in ISO format (YYYY-MM-DD)
      const dateLiteralPattern = /^\d{4}-\d{2}-\d{2}$/
      if (dateLiteralPattern.test(expression)) {
        const dateLiteral = new Date(expression)
        if (!isNaN(dateLiteral.getTime())) {
          const dateStr = dateLiteral.toLocaleDateString()
          text = text.replace(match, dateStr)
          return
        }
      }
    }

    // Check if expression contains math operators (inline calculation)
    if (expression.match(/[+\-*/()]/)) {
      // This is an inline calculation like {weight + 5} or {field1 * field2}
      // Replace field references with their values
      let calcExpression = expression

      // Find all potential field names in the expression
      // Match word characters (field names) but not operators
      const fieldPattern = /\b([a-zA-Z_][a-zA-Z0-9_]*)\b/g
      const fieldNames = expression.match(fieldPattern) || []

      fieldNames.forEach(fieldName => {
        if (hasMissingField) return
        const value = dataModel[fieldName]
        if (value !== undefined && value !== null && value !== '') {
          // Try to parse as number
          const numValue = parseFloat(value)
          if (!isNaN(numValue)) {
            // Replace all instances of this field name with its numeric value
            calcExpression = calcExpression.replace(new RegExp(`\\b${fieldName}\\b`, 'g'), numValue)
          } else {
            // Non-numeric value, mark as missing for calculations
            hasMissingField = true
          }
        } else {
          // If field is empty/undefined, mark as missing
          hasMissingField = true
        }
      })

      // If any referenced field is missing in inline calculation, skip further processing
      if (hasMissingField) {
        return
      }

      // Evaluate the expression safely
      try {
        const safeCalcExpression = calcExpression.replace(/[^0-9+\-*/().\s]/g, '')
        if (safeCalcExpression === calcExpression) {
          // eslint-disable-next-line no-eval
          const result = eval(calcExpression)
          if (!isNaN(result)) {
            // Format the calculated result
            let formattedValue
            if (formatSpec) {
              if (formatSpec.match(/^\.\d+f$/)) {
                const decimals = parseInt(formatSpec.match(/\.(\d+)f$/)[1])
                formattedValue = result.toFixed(decimals)
              } else if (formatSpec === 'int' || formatSpec === 'integer') {
                formattedValue = Math.round(result).toString()
              } else if (formatSpec === 'currency' || formatSpec === '$') {
                formattedValue = '$' + result.toFixed(2)
              } else if (formatSpec === '%' || formatSpec === 'percent') {
                formattedValue = result.toFixed(2) + '%'
              } else {
                formattedValue = result.toString()
              }
            } else {
              if (Number.isInteger(result)) {
                formattedValue = result.toString()
              } else {
                formattedValue = result.toFixed(2)
              }
            }
            text = text.replace(match, formattedValue)
            return
          }
        }
      } catch (error) {
        console.error('Error evaluating inline calculation:', error)
        text = text.replace(match, 'Error')
        return
      }
    }

    // Simple field reference (no calculation)
    const value = dataModel[expression]

    // Handle empty/undefined values - mark as missing
    if (value === undefined || value === null || value === '') {
      hasMissingField = true
      return
    }

    // Try to parse as date first
    const dateValue = parseDate(value)
    if (dateValue) {
      const dateStr = dateValue.toLocaleDateString()
      text = text.replace(match, dateStr)
      return
    }

    // Check if value is a string (non-numeric text)
    const numValue = parseFloat(value)
    if (isNaN(numValue)) {
      // It's a text value, use as-is
      text = text.replace(match, value.toString())
      return
    }

    // Handle as number
    // Format the number based on format specifier
    let formattedValue
    if (formatSpec) {
      // Handle format specifiers like .2f, .0f, etc.
      if (formatSpec.match(/^\.\d+f$/)) {
        const decimals = parseInt(formatSpec.match(/\.(\d+)f$/)[1])
        formattedValue = numValue.toFixed(decimals)
      } else if (formatSpec === 'int' || formatSpec === 'integer') {
        formattedValue = Math.round(numValue).toString()
      } else if (formatSpec === 'currency' || formatSpec === '$') {
        formattedValue = '$' + numValue.toFixed(2)
      } else if (formatSpec === '%' || formatSpec === 'percent') {
        formattedValue = numValue.toFixed(2) + '%'
      } else {
        formattedValue = numValue.toString()
      }
    } else {
      // Default: show number with reasonable precision
      if (Number.isInteger(numValue)) {
        formattedValue = numValue.toString()
      } else {
        formattedValue = numValue.toFixed(2)
      }
    }

    text = text.replace(match, formattedValue)
  })

  // Return NULL (empty string) if any referenced field was missing
  if (hasMissingField) {
    return ''
  }

  return text
}

/**
 * Calculate the value of a calculated field based on its formula and available data
 * @param {Object} field - The field configuration object with formula and calculatedFormat
 * @param {Object} dataModel - The data object containing values for referenced fields
 * @returns {String} - The calculated and formatted value
 */
export function calculateFieldValue (field, dataModel) {
  if (!field.formula || !field.calculated_field) {
    return ''
  }

  try {
    let formula = field.formula

    // Check if this is a text template (wrapped in quotes)
    const isTextTemplate = (formula.startsWith('"') && formula.endsWith('"')) ||
                          (formula.startsWith("'") && formula.endsWith("'"))

    if (isTextTemplate) {
      // Handle text template with string interpolation
      return calculateTextTemplate(formula, dataModel)
    }

    // Replace field references {field_name} with actual values
    const fieldMatches = formula.match(/{([^}]+)}/g)
    if (!fieldMatches) {
      return 'Invalid formula'
    }

    // First pass: handle special date keywords and date calculations
    const today = new Date()

    // Track if any referenced field is missing
    let hasMissingField = false

    // Replace each field reference with its value
    fieldMatches.forEach(match => {
      if (hasMissingField) return // Skip processing if already found missing field

      const fieldName = match.slice(1, -1) // Remove the curly braces

      // Handle special date keywords
      if (fieldName.toLowerCase() === 'today' || fieldName.toLowerCase() === 'now') {
        // Convert today to days since epoch for calculation
        const daysFromEpoch = Math.floor(today.getTime() / (1000 * 60 * 60 * 24))
        formula = formula.replace(match, daysFromEpoch)
        return
      }

      if (fieldName.toLowerCase() === 'today_date' || fieldName.toLowerCase() === 'current_date') {
        const daysFromEpoch = Math.floor(today.getTime() / (1000 * 60 * 60 * 24))
        formula = formula.replace(match, daysFromEpoch)
        return
      }

      // Check if it's a date literal in ISO format (YYYY-MM-DD)
      const dateLiteralPattern = /^\d{4}-\d{2}-\d{2}$/
      if (dateLiteralPattern.test(fieldName)) {
        const dateLiteral = new Date(fieldName)
        if (!isNaN(dateLiteral.getTime()) && dateLiteral.getFullYear() > 1900 && dateLiteral.getFullYear() < 2100) {
          // Convert date literal to days since epoch for calculation
          const daysFromEpoch = Math.floor(dateLiteral.getTime() / (1000 * 60 * 60 * 24))
          formula = formula.replace(match, daysFromEpoch)
          return
        }
      }

      const value = dataModel[fieldName]

      // Check if field is missing/empty - mark as NULL
      if (value === undefined || value === null || value === '') {
        hasMissingField = true
        return
      }

      // Try to parse as date first
      const dateValue = parseDate(value)
      if (dateValue) {
        // Convert date to days since epoch for calculation
        const daysFromEpoch = Math.floor(dateValue.getTime() / (1000 * 60 * 60 * 24))
        formula = formula.replace(match, daysFromEpoch)
      } else {
        // Try to parse as number
        const numValue = parseFloat(value)
        if (isNaN(numValue)) {
          hasMissingField = true
          return
        }
        formula = formula.replace(match, numValue)
      }
    })

    // Return empty string (NULL) if any referenced field is missing
    if (hasMissingField) {
      return ''
    }

    // Evaluate the formula safely
    // Only allow mathematical operations
    const safeFormula = formula.replace(/[^0-9+\-*/().\s]/g, '')
    if (safeFormula !== formula) {
      return 'Invalid formula'
    }

    // eslint-disable-next-line no-eval
    const result = eval(safeFormula)

    if (isNaN(result)) {
      return 'Error'
    }

    // Format based on calculatedFormat
    return formatCalculatedValue(result, field.calculatedFormat || 'number')
  } catch (error) {
    console.error('Error calculating field value:', error)
    return 'Error'
  }
}

/**
 * Format a calculated value according to the specified format
 * @param {Number|String} value - The numeric value to format or text string
 * @param {String} format - The format type (number, currency, integer, percentage, text)
 * @returns {String} - The formatted value
 */
export function formatCalculatedValue (value, format) {
  switch (format) {
  case 'text':
    // Text values are already formatted in calculateTextTemplate
    return value
  case 'currency':
    return '$' + value.toFixed(2)
  case 'integer':
    return Math.round(value).toString()
  case 'percentage':
    return value.toFixed(2) + '%'
  case 'number':
  default:
    if (value) {
      return value.toFixed(3)
    } else {
      return value
    }
  }
}

/**
 * Validate a formula string
 * @param {String} formula - The formula to validate
 * @returns {Object} - Object with isValid boolean and error message if invalid
 */
export function validateFormula (formula) {
  if (!formula || formula.trim() === '') {
    return {
      isValid: false,
      error: 'Formula cannot be empty'
    }
  }

  // Check if this is a text template (wrapped in quotes)
  const isTextTemplate = (formula.startsWith('"') && formula.endsWith('"')) ||
                        (formula.startsWith("'") && formula.endsWith("'"))

  if (isTextTemplate) {
    // Text templates are always valid if they have matching quotes
    // Field references are optional in text templates
    return {
      isValid: true,
      error: null
    }
  }

  // Check for matching braces
  const openBraces = (formula.match(/{/g) || []).length
  const closeBraces = (formula.match(/}/g) || []).length

  if (openBraces !== closeBraces) {
    return {
      isValid: false,
      error: 'Mismatched curly braces {}'
    }
  }

  // Check if formula has at least one field reference
  const fieldMatches = formula.match(/{([^}]+)}/g)
  if (!fieldMatches || fieldMatches.length === 0) {
    return {
      isValid: false,
      error: 'Formula must reference at least one field using {field_name}'
    }
  }

  return {
    isValid: true,
    error: null
  }
}

/**
 * Extract field names referenced in a formula
 * @param {String} formula - The formula to parse
 * @returns {Array} - Array of field names referenced in the formula
 */
export function extractFormulaFields (formula) {
  if (!formula) return []

  const fieldMatches = formula.match(/{([^}]+)}/g)
  if (!fieldMatches) return []

  return fieldMatches.map(match => match.slice(1, -1))
}
